import { useEffect, useState } from 'react';
import adminApi from '@/lib/adminApi';

const accentOptions = [
  { label: 'Orange to Rose', value: 'from-[#FF6F00] to-[#C2185B]' },
  { label: 'Teal to Green', value: 'from-[#00BFA5] to-[#0F766E]' },
  { label: 'Purple', value: 'from-[#4A148C] to-[#7C2D92]' },
  { label: 'Gold to Orange', value: 'from-[#F59E0B] to-[#FF6F00]' },
];

const emptyForm = {
  label: '',
  title: '',
  text: '',
  stat: '',
  accent: accentOptions[0].value,
  display_order: 0,
  is_active: true,
};

const AdminSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadStories = async () => {
    setLoading(true);
    try {
      const response = await adminApi.get('/admin/success-stories');
      setStories(response.data.stories || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStories();
  }, []);

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setError('');
  };

  const submitStory = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        ...formData,
        display_order: Number(formData.display_order || 0),
      };
      if (editingId) {
        await adminApi.put(`/admin/success-stories/${editingId}`, payload);
      } else {
        await adminApi.post('/admin/success-stories', payload);
      }
      resetForm();
      await loadStories();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to save success story.');
    } finally {
      setSaving(false);
    }
  };

  const editStory = (story) => {
    setEditingId(story.id);
    setFormData({
      label: story.label || '',
      title: story.title || '',
      text: story.text || '',
      stat: story.stat || '',
      accent: story.accent || accentOptions[0].value,
      display_order: story.display_order || 0,
      is_active: Boolean(story.is_active),
    });
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteStory = async (storyId) => {
    if (!window.confirm('Delete this success story card?')) return;
    await adminApi.delete(`/admin/success-stories/${storyId}`);
    await loadStories();
  };

  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#FF6F00]">Homepage content</p>
        <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Success Stories</h2>
            <p className="mt-3 max-w-3xl text-gray-600">
              Manage the Early Wins cards shown on the homepage. Keep them short, credible, and launch-friendly.
            </p>
          </div>
          <button type="button" onClick={resetForm} className="btn-outline">
            Add New Story
          </button>
        </div>
      </section>

      <form onSubmit={submitStory} className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg sm:p-8">
        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{editingId ? 'Edit success story' : 'Add success story'}</h3>
            <p className="text-gray-600">Label, title, and story text are required. Stat and color are optional.</p>
          </div>
          {editingId && (
            <button type="button" onClick={resetForm} className="btn-outline">
              Cancel Edit
            </button>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-4">
          <input
            value={formData.label}
            onChange={(event) => updateField('label', event.target.value)}
            placeholder="Label, e.g. Artist ready"
            className="rounded-2xl border border-gray-300 px-4 py-3"
            required
          />
          <input
            value={formData.stat}
            onChange={(event) => updateField('stat', event.target.value)}
            placeholder="Small stat, e.g. PMB ready"
            className="rounded-2xl border border-gray-300 px-4 py-3"
          />
          <select
            value={formData.accent}
            onChange={(event) => updateField('accent', event.target.value)}
            className="rounded-2xl border border-gray-300 px-4 py-3"
          >
            {accentOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            type="number"
            value={formData.display_order}
            onChange={(event) => updateField('display_order', event.target.value)}
            placeholder="Display order"
            className="rounded-2xl border border-gray-300 px-4 py-3"
          />
        </div>

        <input
          value={formData.title}
          onChange={(event) => updateField('title', event.target.value)}
          placeholder="Story title"
          className="mt-4 w-full rounded-2xl border border-gray-300 px-4 py-3"
          required
        />

        <textarea
          value={formData.text}
          onChange={(event) => updateField('text', event.target.value)}
          placeholder="Short story text"
          className="mt-4 min-h-[120px] w-full rounded-2xl border border-gray-300 px-4 py-3"
          required
        />

        <label className="mt-4 inline-flex items-center gap-3 text-sm font-semibold text-gray-700">
          <input
            type="checkbox"
            checked={formData.is_active}
            onChange={(event) => updateField('is_active', event.target.checked)}
          />
          Active on homepage
        </label>

        {error && <p className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

        <button type="submit" disabled={saving} className="btn-primary mt-5 disabled:cursor-not-allowed disabled:bg-gray-300">
          {saving ? 'Saving...' : editingId ? 'Save Story Changes' : 'Create Story'}
        </button>
      </form>

      <section className="rounded-[2rem] border border-orange-100 bg-white shadow-lg">
        {loading ? (
          <div className="p-6 text-gray-500">Loading success stories...</div>
        ) : stories.length === 0 ? (
          <div className="p-6 text-gray-500">No success stories added yet. The homepage will use safe fallback cards.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {stories.map((story) => (
              <article key={story.id} className="grid gap-5 p-6 lg:grid-cols-[1fr_auto] lg:items-center">
                <div>
                  <div className={`mb-4 h-2 w-24 rounded-full bg-gradient-to-r ${story.accent || accentOptions[0].value}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-900">{story.title}</h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${story.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-700'}`}>
                      {story.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-bold text-[#4A148C]">
                      Order {story.display_order || 0}
                    </span>
                  </div>
                  <p className="mt-2 text-sm font-bold uppercase tracking-[0.18em] text-[#FF6F00]">{story.label}</p>
                  {story.stat && <p className="mt-1 text-sm font-semibold text-[#008C7A]">{story.stat}</p>}
                  <p className="mt-3 max-w-4xl text-gray-600">{story.text}</p>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <button type="button" onClick={() => editStory(story)} className="btn-outline">
                    Edit
                  </button>
                  <button type="button" onClick={() => deleteStory(story.id)} className="rounded-full border border-red-200 px-5 py-2 font-semibold text-red-600 hover:bg-red-50">
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminSuccessStories;
